import get from 'lodash/get';
import { createSelector } from 'reselect';

import {
  getActiveDesign,
  getActiveRevision,
  getIndexedRevisions,
  getProductFor,
  getRenders,
  getRevisionFor,
  getStateSliceFor
} from 'redux/Studio';

import {
  getSwapData
} from 'redux/Studio/SwapProducts/selectors';

import { SwapService } from 'services/LiveSwap';

import { Render } from 'redux/models';

import { FORCE_RENDER_ON_DESIGN_CHANGE_OPTIONS } from './constants';

export const designViewerReducerKey = 'designViewer';

const extractDataFrom = state => getStateSliceFor({ state, reducerKey: designViewerReducerKey });

export const getCurrentActiveRender = () => (dispatch, getState) => {
  return getActiveRender(getState());
};

export const getActiveRender = createSelector(
  (state) => getActiveDesign({ state }),
  (state) => get(extractDataFrom(state), 'activeRenderId', null),
  (state) => getIndexedRevisions(state),
  (state) => getRenders(state),
  (state) => getSwapData(state),
  (state) => getForceRenderOnDesignChange(state),
  (activeDesign, activeRenderId, allRevisions, allRenders, replacement, forceRenderOnDesignChange) => {
    if (!activeDesign) { return null; }

    if (!activeRenderId) {
      // if we just switched designs we need to know if we're looking for the first or last render
      if (forceRenderOnDesignChange === FORCE_RENDER_ON_DESIGN_CHANGE_OPTIONS.FIRST) {
        activeRenderId = activeDesign.firstRender().id;
      }
      if (forceRenderOnDesignChange === FORCE_RENDER_ON_DESIGN_CHANGE_OPTIONS.LAST) {
        activeRenderId = activeDesign.lastRender().id;
      }
    }

    // Get the base render.
    let render = activeDesign.findRenderById(activeRenderId) || activeDesign.firstRender();

    // Handle revisions.
    const currRevisionUuid = activeDesign.revisionUuid();
    if (currRevisionUuid) {
      try {
        let fetchDraftRenderUuid = '';
        let hotspotUuids = [];
        let productIds = [];

        const revisionHistory = activeDesign.revisionHistory();
        for (let revisionUuid of revisionHistory) {
          const revision = allRevisions[revisionUuid] || null;
          const renderUid = revision.renderFor(render.camera_id);
          const revRender = allRenders[renderUid] || null;
          const beauty = revRender.beautyVariant();
          const draft = revRender.draftVariant();
          if (beauty && beauty.isFinished()) {
            render = {
              ...render,
              renderUid,
              imagefile: beauty.imageUrl(),
              hotspots: beauty.hotspots()
            };
            break;
          } else if (draft && draft.isFinished()) {
            render = {
              ...render,
              renderUid,
              imagefile: draft.imageUrl(),
              hotspots: draft.hotspots()
            };

            if (render.view_360) {
              render.panoimagefile = draft.imageUrl();
              render.panoramaViewed = false;
              render.description = `360 View`;
            }
            break;
          } else {
            if (revision.isFromShowroom()) {
              // Showroom renders do not use draft renders at all.
              // TODO: when we convert Bessie revision over to using
              // beauty renders we should only fetch draft renders for
              // liveswap revisions so if condition would change to
              // if (!revision.isFromStudio())
              continue;
            }

            // only push replacement products for the most recent revision per hotspot
            getMostRecentReplacements(revision, hotspotUuids, productIds);

            // Set the draft render that should be fetched.
            // If this render is part of an active swap, then
            // do not request the render.
            if (
              revisionUuid === currRevisionUuid &&
              (
                !replacement ||
                replacement.revisionUuid() !== revisionUuid ||
                replacement.cameraId() !== render.camera_id
              )
            ) {
              fetchDraftRenderUuid = draft.uuid();
            }
          }
        }

        // HACK: this is sooo bad relying on global store
        // and having side effects in selector which is
        // part of the render loop. Need to figure out where/how
        // to move this logic. Maybe in one of the lifecycle methods.
        if (fetchDraftRenderUuid) {
          window.modsyStore.dispatch(
            SwapService.fetchRender({
              cameraId: render.camera_id,
              design: activeDesign,
              hotspotUuids,
              productIds,
              renderUuid: fetchDraftRenderUuid,
              revisionUuid: currRevisionUuid
            })
          );
        }
      } catch (e) {
        // do nothing, fall back to base design render
      }
    }

    return render;
  }
);

export const getMostRecentReplacements = (revision, hotspotUuids, productIds) => {
  const revisionReplacements = revision.replacements();
  const revisionReplacementPrevHotspotUuids = Object.keys(revisionReplacements);

  // Get the replacement hotspots and products associated with this render.
  revisionReplacementPrevHotspotUuids.forEach(hotspotUid => {
    // filter out products that have a more recent product revision
    if (!hotspotUuids.includes(revisionReplacements[hotspotUid].newInstanceId)) {
      productIds.push(revisionReplacements[hotspotUid].productId);
    }
    hotspotUuids.push(hotspotUid);
  });
};

export const getActiveRenderIndex = ({ state }) => {
  const render = getActiveRender(state);
  const activeDesign = getActiveDesign({ state });
  return activeDesign ? activeDesign.renderIndex(render) : -1;
};

const defaultEmptyRenders = [];
export const getCurrentRenders = ({ state }) => {
  const activeDesign = getActiveDesign({ state });
  return (activeDesign && activeDesign.hasRenders())
    ? activeDesign.renders()
    : defaultEmptyRenders;
};

export const getRenderKey = state => {
  return extractDataFrom(state).renderKey;
};

export const getSlideDirection = state => {
  return extractDataFrom(state).slideDirection;
};

export const getProductForPDPTooltip = (state) => {
  const data = extractDataFrom(state);
  const productId = data.activePDPTooltipProductId;
  return getProductFor({ productId, state });
};

export const getActivePDPTooltipProductId = (state) => {
  const data = extractDataFrom(state);
  return data.activePDPTooltipProductId;
};

export const getPDPTooltipAlternatives = (state) => {
  const data = extractDataFrom(state);
  return data.popupAlternatives;
};

export const getAwaitingAlternatives = (state) => {
  const data = extractDataFrom(state);
  return data.awaitingAlternatives;
};

export const getAlternativesError = (state) => {
  const data = extractDataFrom(state);
  return data.alternativesError;
};

export const getHotSpotIndexForPDPTooltip = (state) => {
  const data = extractDataFrom(state);
  const hotSpotIndex = data.activePDPTooltipHotSpotIndex;
  return hotSpotIndex;
};

export const getFullScreenRenderBool = ({ state }) => {
  const data = extractDataFrom(state);
  return data.showFullScreenRender;
};

/** Returns the current view mode. One of VIEW, SHOP, or SWAP. */
export const getStudioViewMode = ({ state }) => {
  const data = extractDataFrom(state);
  return data.studioViewMode;
};

/** Returns the current designs filter. */
export const getDesignsFilter = (state) => {
  const data = extractDataFrom(state);
  return data.designsFilter;
};

/** Returns if hostpots are opent as a Bool. */
export const isHotspotActive = (state) => {
  const data = extractDataFrom(state);
  return data.hotspotActive;
};

/** Returns the panorama open flag. */
export const getPanoramaOpenFlag = state => {
  const data = extractDataFrom(state);
  return data.panoramaViewerOpen;
};

/** Returns the panorama render id. */
export const getPanoramaRenderId = state => {
  const data = extractDataFrom(state);
  return data.panoramaViewerRenderId;
};

export const getAlternativesId = (state) => {
  return extractDataFrom(state).alternativesId;
};

export const getAllowSwipe = (state) => {
  return extractDataFrom(state).allowSwipe;
};

export const getForceRenderOnDesignChange = (state) => {
  return extractDataFrom(state).forceRenderOnDesignChange;
};

export const getMoreMenuOpen = ({ state }) => {
  return extractDataFrom(state).moreMenuOpen;
};

export const getDesignNotification = ({ state }) => {
  return extractDataFrom(state).designNotification;
};

export const getShowMobileHotspots = (state) => extractDataFrom(state).showMobileHotspots;

export const getHasSeenMobileHotspots = (state) => extractDataFrom(state).hasSeenMobileHotspots;

export const getMobileHotspotsView = (state) => extractDataFrom(state).mobileHotspotsView;

export const getCurrentMobileHotspotsView = () => (dispatch, getState) => {
  return getMobileHotspotsView(getState());
};

/** Returns the active revision renders or null. */
export const getActiveRevisionRenders = createSelector(
  state => getActiveRevision(state),
  state => getRenders(state),
  (revision, renders) => {
    if (!revision) { return {}; }
    const renderUids = Object.values(revision.renders());

    return renderUids.reduce((acc, renderUid) => {
      acc[renderUid] = renders[renderUid];
      return acc;
    }, {});
  }
);

/** Returns all the BEAUTY renders for the active revision. */
export const getActiveRevisionBeautyRenders = createSelector(
  state => getActiveRevisionRenders(state),
  (renders) => {
    let beautyRenders = {};

    for (let renderUid of Object.keys(renders)) {
      const beauty = renders[renderUid].beautyVariant();
      beautyRenders[renderUid] = beauty;
    }

    return beautyRenders;
  }
);

/** Returns true if all BEAUTY renders are finished for the active revision. */
export const getAllBeautyRendersFinishedForActiveRevision = createSelector(
  state => getActiveRevisionBeautyRenders(state),
  (beautyRenders) => {
    for (let beautyRender of Object.values(beautyRenders)) {
      if (!beautyRender.isFinished()) {
        return false; // early exit on first non ready beauty.
      }
    }

    // If we get here then all beauty renders are ready
    // or the active revision has no renders at all.
    return true;
  }
);

/** Returns the revision renders of design or null. */
export const getRevisionRenders = createSelector(
  ({ state, revisionUuid }) => getRevisionFor({ state, revisionUuid }),
  ({ state }) => getRenders(state),
  (revision, renders) => {
    if (!revision) { return {}; }
    const renderUids = Object.values(revision.renders());

    return renderUids.reduce((acc, renderUid) => {
      acc[renderUid] = renders[renderUid];
      return acc;
    }, {});
  }
);

/** Returns the active render model instance. */
export const getActiveRenderModel = createSelector(
  state => getActiveRevision(state),
  getActiveRender,
  getActiveRevisionRenders,
  (revision, render, renders) => {
    if (!revision) { return null; }
    const renderUid = Render.createRenderUid({
      revisionUuid: revision.uuid(),
      cameraName: render && render.camera_id
    });
    return renders[renderUid];
  }
);
